import React, { useMemo, useState } from "react";
import { useFamilyData } from "../../hooks/useFamilyData";
import { logAudit } from "../../utils/audit";
import { createActionId, executeAction } from "../../utils/actions";
import { runActionWithQueue } from "../../utils/actionQueue";

export default function ApprovalsCard({ familyId, isPro, canManage }) {
  const { tasks = [], kids = [] } = useFamilyData(familyId);
  const pendingTasks = tasks.filter(t => t.status === "pending");
  const [kidFilter, setKidFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState([]);

  const filteredTasks = useMemo(() => {
    if (kidFilter === "all") return pendingTasks;
    return pendingTasks.filter(t => t.assignedTo === kidFilter);
  }, [pendingTasks, kidFilter]);

  const approveTask = async (task) => {
    if (!canManage) return;
    const actionId = createActionId();
    const { queued } = await runActionWithQueue(
      { type: "task.approve", familyId, taskId: task.id, actionId },
      executeAction
    );
    if (!queued) {
      await logAudit({
        familyId,
        action: "approve",
        entity: "task",
        meta: { taskId: task.id, kidId: task.assignedTo }
      });
    }
  };

  const approveSelected = async () => {
    const tasksToApprove = filteredTasks.filter(t => selectedIds.includes(t.id));
    for (const task of tasksToApprove) {
      await approveTask(task);
    }
    setSelectedIds([]);
  };

  const failSelected = async () => {
    const tasksToFail = filteredTasks.filter(t => selectedIds.includes(t.id));
    for (const task of tasksToFail) {
      await failTask(task);
    }
    setSelectedIds([]);
  };

  const toggleSelected = (taskId) => {
    setSelectedIds(prev =>
      prev.includes(taskId) ? prev.filter(id => id !== taskId) : [...prev, taskId]
    );
  };

  const failTask = async (task) => {
    if (!canManage) return;
    const actionId = createActionId();
    const { queued } = await runActionWithQueue(
      { type: "task.fail", familyId, taskId: task.id, actionId, message: "" },
      executeAction
    );
    if (!queued) {
      await logAudit({
        familyId,
        action: "fail",
        entity: "task",
        meta: { taskId: task.id, kidId: task.assignedTo }
      });
    }
  };

  return (
    <div className="glass parent-card approvals-card" data-tour="parent-approvals-card">
      <div className="header-flex">
        <h2>Approvals</h2>
        {pendingTasks.length > 0 && <span className="badge">{pendingTasks.length} Pending</span>}
      </div>
      <div className="card-body">
        {pendingTasks.length === 0 ? (
          <div style={{ textAlign: "center", opacity: 0.75, display: "grid", gap: 8 }}>
            <p style={{ margin: 0 }}>No chores waiting for approval. 👍</p>
            <button
              className="btn-nav"
              type="button"
              onClick={() =>
                document.querySelector("[data-tour='parent-task-card']")?.scrollIntoView({ behavior: "smooth", block: "center" })
              }
            >
              Assign chores
            </button>
          </div>
        ) : !canManage ? (
          <p style={{ textAlign: "center", opacity: 0.6 }}>You have view-only access.</p>
        ) : !isPro ? (
          pendingTasks.map(t => (
            <div key={t.id} className="card approval-item">
              <div>
                <div className="approval-person">
                   👤 {t.kidName || "Someone"}
                </div>
                <strong>{t.title}</strong>
                <div style={{ color: 'var(--primary)', fontWeight: 'bold' }}>⭐ {t.points} Points</div>
              </div>
              <div className="approval-actions approval-actions-two">
                <button className="btn-nav success-btn" onClick={() => approveTask(t)}>Approve</button>
                <button className="btn-nav" style={{ color: 'var(--danger)', fontWeight: '600' }} onClick={() => failTask(t)}>Fail</button>
              </div>
            </div>
          ))
        ) : (
          <>
            <div className="approval-bulk-row">
              <select value={kidFilter} onChange={(e) => setKidFilter(e.target.value)}>
                <option value="all">All Kids</option>
                {kids.map(k => (
                  <option key={k.id} value={k.id}>{k.name}</option>
                ))}
              </select>
              <button
                className="btn-nav"
                onClick={() =>
                  setSelectedIds(filteredTasks.map(t => t.id))
                }
              >
                Select All
              </button>
              <button
                className="btn-nav success-btn"
                disabled={selectedIds.length === 0}
                onClick={approveSelected}
              >
                Approve Selected
              </button>
              <button
                className="btn-nav"
                style={{ color: "var(--danger)", fontWeight: 600 }}
                disabled={selectedIds.length === 0}
                onClick={failSelected}
              >
                Fail Selected
              </button>
            </div>

            {filteredTasks.map(t => (
              <div key={t.id} className="card approval-item">
                <div>
                  {/* DISPLAY KID NAME */}
                  <div className="approval-person">
                     👤 {t.kidName || "Someone"}
                  </div>
                  <strong>{t.title}</strong>
                  <div style={{ color: 'var(--primary)', fontWeight: 'bold' }}>⭐ {t.points} Points</div>
                </div>
                <div className="approval-actions approval-actions-three">
                  <button className="btn-nav success-btn" onClick={() => approveTask(t)}>Approve</button>
                  <button className="btn-nav" style={{ color: 'var(--danger)', fontWeight: '600' }} onClick={() => failTask(t)}>Fail</button>
                  <button className="btn-nav" onClick={() => toggleSelected(t.id)}>
                    {selectedIds.includes(t.id) ? "Selected" : "Select"}
                  </button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
